import React, { useEffect, useState } from "react";
import { api, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatDateTime } from "@/lib/format";
import { toast } from "sonner";
import { ShieldCheck, UserPlus, KeyRound, Loader2 } from "lucide-react";

/**
 * Admin-kontoer: liste, opprett ny admin, sett passord på en admin.
 * Kundekontoer kan aldri få passord satt herfra (backend håndhever).
 */
export const AdminAccountsCard = () => {
  const [items, setItems] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [pwTarget, setPwTarget] = useState(null);
  const [form, setForm] = useState({ email: "", full_name: "Administrator", password: "", confirm: "" });
  const [pw, setPw] = useState({ new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/admins").then((r) => setItems(r.data.items)).catch((e) => toast.error(errorMessage(e)));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (form.password.length < 10) { toast.error("Passordet må ha minst 10 tegn"); return; }
    if (form.password !== form.confirm) { toast.error("Passordene er ikke like"); return; }
    setBusy(true);
    try {
      await api.post("/admin/admins", { email: form.email.trim(), full_name: form.full_name.trim() || "Administrator", password: form.password });
      toast.success(`Admin-konto ${form.email.trim()} opprettet`);
      setCreateOpen(false);
      setForm({ email: "", full_name: "Administrator", password: "", confirm: "" });
      load();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  const setPassword = async (e) => {
    e.preventDefault();
    if (pw.new_password.length < 10) { toast.error("Passordet må ha minst 10 tegn"); return; }
    if (pw.new_password !== pw.confirm) { toast.error("Passordene er ikke like"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/admins/${pwTarget.id}/password`, { new_password: pw.new_password });
      toast.success(`Nytt passord satt for ${pwTarget.email}`);
      setPwTarget(null);
      setPw({ new_password: "", confirm: "" });
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  return (
    <Card data-testid="admin-accounts-card">
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <CardTitle className="text-base flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-primary" /> Admin-kontoer</CardTitle>
            <CardDescription>Driftskontoer uten tilgang til kundedata. Opprett en ny admin eller sett nytt passord hvis noen er låst ute.</CardDescription>
          </div>
          <Button size="sm" onClick={() => setCreateOpen(true)} data-testid="admin-create-admin-button"><UserPlus className="h-4 w-4 mr-2" /> Ny admin</Button>
        </div>
      </CardHeader>
      <CardContent className="p-0 overflow-x-auto">
        {!items ? <div className="p-4"><Skeleton className="h-16" /></div> : (
          <Table data-testid="admin-accounts-table">
            <TableHeader><TableRow><TableHead className="pl-4">Konto</TableHead><TableHead className="hidden md:table-cell">Sist innlogget</TableHead><TableHead>Status</TableHead><TableHead className="text-right pr-4">Handling</TableHead></TableRow></TableHeader>
            <TableBody>
              {items.map((a) => (
                <TableRow key={a.id} className="h-14" data-testid={`admin-account-row-${a.id}`}>
                  <TableCell className="pl-4"><p className="font-medium">{a.full_name || "–"} {a.is_me ? <Badge variant="secondary" className="ml-1">deg</Badge> : null}</p><p className="text-xs text-muted-foreground">{a.email}</p></TableCell>
                  <TableCell className="hidden md:table-cell text-sm tnum">{a.last_login_at ? formatDateTime(a.last_login_at) : "Aldri"}</TableCell>
                  <TableCell>{a.is_active ? <span className="text-xs font-medium text-success">Aktiv</span> : <span className="text-xs font-medium text-destructive">Deaktivert</span>}</TableCell>
                  <TableCell className="text-right pr-4"><Button variant="outline" size="sm" onClick={() => setPwTarget(a)} data-testid={`admin-set-password-${a.id}`}><KeyRound className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Sett passord</span></Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <Dialog open={createOpen} onOpenChange={(o) => !busy && setCreateOpen(o)}>
        <DialogContent className="bg-card" data-testid="admin-create-admin-dialog">
          <DialogHeader><DialogTitle>Ny admin-konto</DialogTitle><DialogDescription>Kontoen får kun tilgang til admin-panelet. Bruk en egen driftsadresse, ikke en kundes e-post.</DialogDescription></DialogHeader>
          <form onSubmit={create} className="space-y-3">
            <div className="space-y-1.5"><Label htmlFor="na_email">E-post</Label><Input id="na_email" type="email" required autoComplete="off" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="admin@controlle.no" data-testid="admin-create-email" /></div>
            <div className="space-y-1.5"><Label htmlFor="na_name">Navn</Label><Input id="na_name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="admin-create-name" /></div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label htmlFor="na_pw">Passord (min. 10 tegn)</Label><Input id="na_pw" type="password" autoComplete="new-password" minLength={10} required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="admin-create-password" /></div>
              <div className="space-y-1.5"><Label htmlFor="na_pw2">Gjenta passord</Label><Input id="na_pw2" type="password" autoComplete="new-password" required value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} data-testid="admin-create-confirm" /></div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)} disabled={busy} data-testid="admin-create-cancel">Avbryt</Button>
              <Button type="submit" disabled={busy} data-testid="admin-create-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserPlus className="h-4 w-4 mr-2" />} Opprett admin</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pwTarget} onOpenChange={(o) => { if (!busy && !o) { setPwTarget(null); setPw({ new_password: "", confirm: "" }); } }}>
        <DialogContent className="bg-card" data-testid="admin-set-password-dialog">
          <DialogHeader><DialogTitle>Sett nytt passord</DialogTitle><DialogDescription>For <span className="font-medium text-foreground">{pwTarget?.email}</span>. Kontoen reaktiveres om den var deaktivert. Handlingen logges.</DialogDescription></DialogHeader>
          <form onSubmit={setPassword} className="space-y-3">
            <div className="space-y-1.5"><Label htmlFor="sp_pw">Nytt passord (min. 10 tegn)</Label><Input id="sp_pw" type="password" autoComplete="new-password" minLength={10} required value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} data-testid="admin-set-password-input" /></div>
            <div className="space-y-1.5"><Label htmlFor="sp_pw2">Gjenta passord</Label><Input id="sp_pw2" type="password" autoComplete="new-password" required value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} data-testid="admin-set-password-confirm" /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPwTarget(null)} disabled={busy} data-testid="admin-set-password-cancel">Avbryt</Button>
              <Button type="submit" disabled={busy} data-testid="admin-set-password-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <KeyRound className="h-4 w-4 mr-2" />} Sett passord</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
